import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Broom, CaretDown, CaretRight, Sparkle, Trash, WarningCircle } from "@phosphor-icons/react";
import { DataQualityTools } from "../components/DataQualityTools";
import { ReplyLoggerCard } from "../components/ReplyLoggerCard";
import { RescueCard } from "../components/RescueCard";
import { MoveDetectorCard } from "../components/MoveDetectorCard";

export default function CleanupPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(new Set());
  const [sel, setSel] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.cleanupPreview();
      setData(d);
      const pre = new Set();
      d.categories.forEach(cat => cat.contacts.forEach(c => { if (!(c.engagement_score > 0)) pre.add(c.id); }));
      setSel(pre);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const startScan = async () => {
    try {
      const r = await api.cleanupAiScan();
      setJob({ id: r.job_id, status: "running", progress: { done: 0, total: 0 } });
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.cleanupAiStatus(r.job_id);
          setJob(s);
          if (s.status !== "running") {
            clearInterval(pollRef.current);
            if (s.status === "done") {
              toast.success(`AI scan complete — ${s.found} unusual titles flagged`);
              load();
            } else {
              toast.error(`AI scan failed: ${s.error || "unknown error"}`);
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleCat = (key) => setOpen(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleOne = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setCat = (cat, on) => setSel(prev => {
    const n = new Set(prev);
    cat.contacts.forEach(c => on ? n.add(c.id) : n.delete(c.id));
    return n;
  });

  const selectedInCat = (cat) => cat.contacts.filter(c => sel.has(c.id)).length;

  const doDelete = async () => {
    if (!sel.size) return;
    if (!window.confirm(`Delete ${sel.size.toLocaleString()} contacts? They will be archived for 30 days first, so this is recoverable.`)) return;
    setDeleting(true);
    try {
      const r = await api.cleanupDelete([...sel]);
      toast.success(`${r.deleted.toLocaleString()} contacts deleted (archived copy kept)`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDeleting(false);
    }
  };

  const totals = useMemo(() => data ? { flagged: data.flagged_total, total: data.total_contacts } : null, [data]);

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Contact cleanup</h2>
          <p className="text-sm text-zinc-500 mt-0.5 max-w-2xl">
            Contacts whose job titles are unlikely to ever commission fieldwork to Medicys, grouped with the reason. Review each group, untick anyone you want to keep, then delete. People who have engaged with your campaigns are highlighted and left unticked by default.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {job?.status === "running" ? (
            <div className="text-sm text-zinc-600 rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid="cleanup-ai-progress">
              <Sparkle size={14} className="inline mr-1 animate-pulse" />
              AI scanning titles… batch {job.progress?.done || 0} of {job.progress?.total || "?"} · {job.found || 0} flagged
            </div>
          ) : (
            <button onClick={startScan} data-testid="cleanup-ai-scan-btn"
              className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 flex items-center gap-1.5">
              <Sparkle size={14} /> Run AI title scan
            </button>
          )}
        </div>
      </div>

      <DataQualityTools />

      <ReplyLoggerCard />

      <MoveDetectorCard />

      <RescueCard />

      {totals && (
        <div className="text-sm text-zinc-600" data-testid="cleanup-summary">
          <span className="font-medium text-zinc-900">{totals.flagged.toLocaleString()}</span> of {totals.total.toLocaleString()} contacts flagged · the AI scan reviews every other distinct job title with GPT to catch unusual ones the rules miss
        </div>
      )}

      {loading && <div className="rounded-lg border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">Analysing job titles…</div>}

      {!loading && data && data.categories.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-white p-12 text-center text-zinc-400">
          <Broom size={32} className="mx-auto mb-2" />
          <p className="text-sm">Nothing flagged — your database looks clean. Run the AI title scan to double-check unusual titles.</p>
        </div>
      )}

      {!loading && data && data.categories.map(cat => {
        const isOpen = open.has(cat.key);
        const nSel = selectedInCat(cat);
        const allSel = nSel === cat.contacts.length && cat.contacts.length > 0;
        return (
          <div key={cat.key} className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid={`cleanup-cat-${cat.key.replace(/[^a-z0-9]+/gi, "-")}`}>
            <div className="px-3 py-2.5 flex items-center gap-2 cursor-pointer hover:bg-zinc-50" onClick={() => toggleCat(cat.key)}>
              {isOpen ? <CaretDown size={14} className="text-zinc-400" /> : <CaretRight size={14} className="text-zinc-400" />}
              <span className="font-medium text-sm text-zinc-900">{cat.name}</span>
              <span className={`text-[11px] px-1.5 py-0.5 rounded border ${cat.source === "ai" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>{cat.source === "ai" ? "AI" : "rules"}</span>
              <span className="text-sm text-zinc-500">{cat.count.toLocaleString()} contact{cat.count === 1 ? "" : "s"}</span>
              {cat.engaged_count > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600"><WarningCircle size={13} /> {cat.engaged_count} engaged — left unticked</span>
              )}
              <span className="ml-auto text-xs text-zinc-400">{nSel} selected</span>
              <button onClick={(e) => { e.stopPropagation(); setCat(cat, !allSel); }} data-testid={`cleanup-cat-toggle-${cat.key.replace(/[^a-z0-9]+/gi, "-")}`}
                className="text-xs px-2 py-1 rounded border border-zinc-200 text-zinc-600 hover:border-zinc-400">{allSel ? "Untick all" : "Tick all"}</button>
            </div>
            <div className="px-3 pb-2 -mt-1 text-xs text-zinc-500 pl-9">{cat.reason}</div>
            {isOpen && (
              <div className="border-t border-zinc-100 max-h-96 overflow-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {cat.contacts.map(c => (
                      <tr key={c.id} className={`border-b border-zinc-100 last:border-0 ${c.engagement_score > 0 ? "bg-amber-50/50" : ""}`}>
                        <td className="px-3 py-1.5 w-8"><input type="checkbox" checked={sel.has(c.id)} onChange={() => toggleOne(c.id)} data-testid={`cleanup-check-${c.id}`} /></td>
                        <td className="px-3 py-1.5 font-medium text-zinc-900 whitespace-nowrap">{c.first_name} {c.last_name}</td>
                        <td className="px-3 py-1.5 text-zinc-500 font-mono text-[12px]">{c.email}</td>
                        <td className="px-3 py-1.5 text-zinc-600 whitespace-nowrap">{c.company}</td>
                        <td className="px-3 py-1.5 text-zinc-600 max-w-[260px] truncate" title={c.job_title}>{c.job_title}</td>
                        <td className="px-3 py-1.5 text-xs whitespace-nowrap">{c.engagement_score > 0 ? <span className="text-amber-600 font-medium">score {c.engagement_score}</span> : <span className="text-zinc-300">0</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {sel.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-xl border border-zinc-200 bg-white shadow-lg px-4 py-2.5 flex items-center gap-3">
          <span className="text-sm text-zinc-700"><span className="font-medium">{sel.size.toLocaleString()}</span> selected for deletion</span>
          <button onClick={doDelete} disabled={deleting} data-testid="cleanup-delete-btn"
            className="h-9 px-4 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40 flex items-center gap-1.5">
            <Trash size={14} />{deleting ? "Deleting…" : "Delete selected"}
          </button>
        </div>
      )}
    </div>
  );
}
